import React, { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { isEmpty, orderBy } from 'lodash'
import {
  Box,
  Button,
  Typography,
  TableContainer,
  Table,
  TableBody,
  TableRow,
  TableCell,
  makeStyles,
  Theme,
  Divider,
  Hidden,
} from '@material-ui/core'
import { generatePath } from 'react-router-dom'
import { useCopyBtnStyles } from '../../commonStyles'
import {
  LegalEntityDirector,
  useRemoveLegalEntityDirectorMutation,
  useGetDirectorsQuery,
  GetDirectorsDocument,
  LimitedAccessRight,
  OrderDirection,
} from '../../graphql'
import { directorsStateVar } from '../../graphql/local'
import {
  useCustomHistory,
  useDetermineUserRights,
  useMatchCurrentLocation,
  usePageFiltersSorting,
} from '../../hooks'
import { APP_PATHS } from '../../routes/paths'
import { AlertMsgRequisites } from '../../types'
import { ConfirmationAnyModal } from '../Common'
import Attention from '../../assets/images/icons/attention.svg?react'
import IconDot from '../../assets/images/icons/Dot.svg?react'
import { CopyToClipboard } from '../Common/CopyToClipboard'
import { DirectorsMobileRow } from './DirectorsMobileRow'
import { DirectorsTableHeader } from './DirectorsTableHeader'
import { DirectorsTabletRow } from './DirectorsTabletRow'

const useStyles = makeStyles((theme: Theme) => ({
  table: {
    borderTop: '1px solid rgba(0,0,0,.1)',
  },
  buttonsBox: {
    display: 'flex',
    alignItems: 'center',
    margin: theme.spacing(0, -1),
    '& .MuiButton-root': {
      minWidth: 10,
      minHeight: 'auto',
      fontSize: '0.875rem',
      fontWeight: '400',
      lineHeight: '1.5rem',
    },
    '& .MuiButton-label': {
      textDecoration: 'underline',
    },
  },
  cell: {
    whiteSpace: 'nowrap',
  },
  mail: {
    color: 'inherit',
  },
  tableRow: {
    cursor: 'pointer',
  },
}))

export const DirectorsTable: React.FC<{
  contractId: number | string
  allowToDelete?: boolean
  onEdit: (directorId: string | number) => void
}> = ({ contractId, onEdit, allowToDelete }) => {
  const { t } = useTranslation()
  const classes = { ...useStyles(), ...useCopyBtnStyles() }
  const { push, initHistory } = useCustomHistory()

  const { pageFilters } = usePageFiltersSorting(directorsStateVar)
  const { sortBy, direction } = pageFilters

  const [openDelete, setOpenDelete] = useState<string | number | undefined>()

  const [removeLegalEntityDirector] = useRemoveLegalEntityDirectorMutation()

  const { userRights } = useDetermineUserRights(contractId)
  const isViewOnly = userRights?.limitedAccessRight === LimitedAccessRight.ViewOnly

  const isReviewPage = useMatchCurrentLocation(APP_PATHS.application.review)
  const isDirectorsTab = useMatchCurrentLocation(APP_PATHS.dashboard.contractManagement.people)

  const { data, loading: directorsLoading } = useGetDirectorsQuery({
    variables: { id: contractId },
    fetchPolicy: 'network-only',
    skip: !contractId,
  })

  const directorsData = useMemo(() => {
    if (
      data?.contract?.owner &&
      'legalEntityDirectors' in data.contract.owner &&
      !!data.contract.owner.legalEntityDirectors?.length
    ) {
      const orderDirection = direction === OrderDirection.Ascending ? 'asc' : 'desc'

      return orderBy(
        data.contract.owner.legalEntityDirectors,
        sortBy,
        orderDirection,
      ) as LegalEntityDirector[]
    }
    return []
  }, [data?.contract?.owner, directorsLoading, sortBy, direction])

  const onConfirmDelete = useCallback(async (id) => {
    await removeLegalEntityDirector({
      variables: {
        id,
      },
      refetchQueries: [
        {
          query: GetDirectorsDocument,
          variables: { id: contractId },
        },
      ],
      awaitRefetchQueries: true,
    })
    setOpenDelete(undefined)
  }, [])

  const watchDetails = (directorId: string | number) => () => {
    if (!isDirectorsTab) return
    initHistory()
    push(
      generatePath(APP_PATHS.dashboard.contractManagement.directorOverview, {
        applicationId: contractId,
        directorId,
      }),
    )
  }

  const handleEdit = (directorId: string | number) => (e: React.SyntheticEvent) => {
    e.stopPropagation()
    onEdit(directorId)
  }

  if (isEmpty(directorsData)) {
    return isReviewPage ? <Divider /> : null
  }

  return directorsLoading ? null : (
    <>
      <Hidden mdDown>
        <TableContainer>
          <Table className={classes.table}>
            <DirectorsTableHeader allowToDelete={allowToDelete} />

            <TableBody>
              {directorsData.map((director) => {
                return (
                  <TableRow
                    hover
                    key={director.id}
                    data-test="personAccount"
                    onClick={watchDetails(director.id)}
                    className={isDirectorsTab ? classes.tableRow : ''}
                  >
                    <TableCell>
                      <Typography noWrap>{director.individual?.fullName}</Typography>
                    </TableCell>

                    <TableCell className={classes.cell}>{t(director.role || '')}</TableCell>
                    <TableCell className={classes.cell}>{t(director.signingRight || '')}</TableCell>
                    <TableCell className={classes.cell}>
                      <a
                        className={classes.mail}
                        href={`mailto:${director.individual?.email}`}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {director.individual?.email}
                      </a>
                    </TableCell>
                    <TableCell>
                      <CopyToClipboard
                        address={director.individual?.phone}
                        msgType={AlertMsgRequisites.phone}
                      />
                    </TableCell>

                    <TableCell width={168}>
                      {!isViewOnly && (
                        <Box className={classes.buttonsBox}>
                          <Button aria-label="Edit" type="button" onClick={handleEdit(director.id)}>
                            {t('edit', 'Edit')}
                          </Button>
                          {allowToDelete && (
                            <>
                              <IconDot />
                              <Button
                                color="secondary"
                                aria-label="delete"
                                type="button"
                                onClick={() => setOpenDelete(director.id)}
                                data-test="personsAccountsDelete-btn"
                              >
                                {t('delete', 'Delete')}
                              </Button>
                            </>
                          )}
                        </Box>
                      )}
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Hidden>

      {/* mobile tablet view */}

      <Hidden xsDown lgUp>
        {directorsData.map((director) => (
          <DirectorsTabletRow key={director.id} director={director} handleDetails={watchDetails}>
            {!isViewOnly && (
              <Box className={classes.buttonsBox}>
                <Button aria-label="Edit" type="button" onClick={handleEdit(director.id)}>
                  {t('edit', 'Edit')}
                </Button>
                {allowToDelete && (
                  <>
                    <IconDot />
                    <Button
                      color="secondary"
                      aria-label="delete"
                      type="button"
                      onClick={() => setOpenDelete(director.id)}
                      data-test="personsAccountsDelete-btn"
                    >
                      {t('delete', 'Delete')}
                    </Button>
                  </>
                )}
              </Box>
            )}
          </DirectorsTabletRow>
        ))}
      </Hidden>

      {/* mobile table */}
      <Hidden smUp>
        {directorsData.map((director) => (
          <DirectorsMobileRow key={director.id} director={director} handleDetails={watchDetails}>
            {!isViewOnly && (
              <Box className={classes.buttonsBox}>
                <Button aria-label="Edit" type="button" onClick={handleEdit(director.id)}>
                  {t('edit', 'Edit')}
                </Button>
                {allowToDelete && (
                  <>
                    <IconDot />
                    <Button
                      color="secondary"
                      aria-label="delete"
                      type="button"
                      onClick={() => setOpenDelete(director.id)}
                      data-test="personsAccountsDelete-btn"
                    >
                      {t('delete', 'Delete')}
                    </Button>
                  </>
                )}
              </Box>
            )}
          </DirectorsMobileRow>
        ))}
      </Hidden>

      <ConfirmationAnyModal
        title={t('delete', 'Delete')}
        color={'error'}
        icon={<Attention />}
        name="delete"
        handleClose={() => setOpenDelete(undefined)}
        handleConfirm={() => onConfirmDelete(openDelete)}
        isOpen={!!openDelete}
        labelCancel={t('cancel', 'Cancel')}
        labelConfirm={t('delete', 'Delete')}
      >
        <Typography>{t('deleteDirectorConfirm')}</Typography>
      </ConfirmationAnyModal>
    </>
  )
}
